/**
 * "THE INVITATION EXISTS. THE MESSAGE DID NOT GO OUT." — the 202 path, in Hebrew.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * A GREEN TICK OVER AN UNDELIVERED MESSAGE IS A SUPPORT CALL WITH A DELAY FUSE.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * `routes/customer-admin.ts` says why the status is 202 and not 200:
 *
 *   > *a caller who is told 200 when nobody receives anything has no way to tell that from a spam
 *   > folder — so the delivery outcome is surfaced.*
 *
 * That is the whole of this module's job. The office manager who invited somebody has to be able to
 * tell three situations apart, and they are three different actions on three different days:
 *
 *   ACCEPTED   the provider took the message. Nothing to do but wait.
 *   SUPPRESSED nobody sent it, on purpose — this build has no mail path. The invitation is real and
 *              a person must be told out of band. This is NOT a failure and must not read as one.
 *   FAILED     the provider refused it. The address or the mail path is wrong, and resending the
 *              same message to the same address will fail the same way.
 *
 * ── WHAT IS TRUE IN ALL THREE ───────────────────────────────────────────────────────────────────
 *
 * THE INVITATION WAS WRITTEN. The row exists, the token was minted, the seat is consumed. Every
 * sentence below says so, because an administrator who reads "not delivered" and concludes "so I
 * should invite them again" creates a second invitation and a second seat charge for one person.
 *
 * ── AND WHAT NEVER APPEARS ──────────────────────────────────────────────────────────────────────
 *
 * The token. `mintInvitationToken` stores only a SHA-256, the clear token reaches the invitee
 * through the mail port and NO API caller, and there is no field on any type here that could carry
 * one. The remedy for a suppressed message is therefore "resend", never "read the link to them".
 *
 * This module is pure. It maps an outcome the server reported into words, and decides nothing.
 */

import type { InvitationResult, MailDeliveryReason } from './contract';

export interface DeliveryText {
  readonly titleHe: string;
  /** What happened, and what is nonetheless true. Both halves, every time. */
  readonly explanationHe: string;
  /** 09 §5.8: one written next step, and it is never "try again". */
  readonly nextStepHe: string;
  readonly tone: 'ok' | 'warn' | 'error';
  /** `true` ⇒ resending the SAME message to the SAME address would produce the same outcome. */
  readonly resendWouldRepeat: boolean;
}

const ACCEPTED_HE: DeliveryText = {
  titleHe: 'ההזמנה נשלחה',
  explanationHe:
    'ההזמנה נרשמה והמסר נמסר לשירות הדואר. ההצטרפות נעשית בידי המוזמן, מהקישור שנשלח אליו.',
  nextStepHe: 'המוזמן יופיע ברשימת המשתמשים במצב ״הוזמן״ עד שיצטרף.',
  tone: 'ok',
  resendWouldRepeat: false,
};

/**
 * The two undelivered outcomes.
 *
 * They share the first sentence — the invitation exists — and diverge on the second, because the
 * person reading them has to do two different things. Collapsing them into "לא נשלח" would tell an
 * office to check an address that is perfectly correct.
 */
const DELIVERY_HE: Readonly<Record<MailDeliveryReason, DeliveryText>> = {
  mail_suppressed: {
    titleHe: 'ההזמנה נרשמה — ההודעה לא נשלחה',
    explanationHe:
      'ההזמנה קיימת במערכת והמושב שלה נתפס, אך שליחת ההודעות מושבתת בבנייה הזו ואיש לא קיבל דבר. ' +
      'זו אינה תקלה בכתובת ואינה תקלה בשליחה — פשוט אין נתיב דואר פעיל.',
    nextStepHe:
      'יש ליידע את המוזמן בדרך אחרת שההזמנה ממתינה לו, ולשלוח אותה שוב כאשר נתיב הדואר יופעל. ' +
      'אין ליצור הזמנה נוספת לאותו אדם — היא תתפוס מושב שני.',
    tone: 'warn',
    // Nothing about the message is wrong; the path is off. A resend now behaves identically.
    resendWouldRepeat: true,
  },
  mail_failed: {
    titleHe: 'ההזמנה נרשמה — ההודעה נדחתה',
    explanationHe:
      'ההזמנה קיימת במערכת והמושב שלה נתפס, אך שירות הדואר סירב לקבל את ההודעה. ' +
      'לרוב מדובר בכתובת שגויה או בכתובת שאינה מקבלת דואר.',
    nextStepHe:
      'יש לבדוק את כתובת הדואר של המוזמן. שליחה חוזרת לאותה כתובת תיכשל באותו אופן, ולכן יש לתקן ' +
      'את הכתובת לפני שליחה נוספת.',
    tone: 'error',
    resendWouldRepeat: true,
  },
};

/** Exposed for the mirror test only — an unmapped outcome is an English code on a Hebrew screen. */
export function mappedDeliveryReasons(): readonly string[] {
  return Object.keys(DELIVERY_HE);
}

/**
 * The delivery outcome, rendered. `null` is the 200 path and gets the accepted text — the absence of
 * a `delivery` field is a positive statement, not missing data.
 */
export function deliveryTextFor(delivery: MailDeliveryReason | null): DeliveryText {
  return delivery === null ? ACCEPTED_HE : DELIVERY_HE[delivery];
}

/**
 * A whole invitation outcome as one written state.
 *
 * The expiry is part of it, and stated as a fact rather than as a warning: a resend MINTS A NEW
 * TOKEN and the previous one stops resolving, so an office that sent two links needs to know that
 * only the last one works. `null` expiry says so rather than showing a date the server did not give.
 */
export interface InvitationOutcomeText extends DeliveryText {
  /** `null` ⇒ the server stated no expiry. Never rendered as "never" and never as a zero date. */
  readonly expiryHe: string | null;
}

export function invitationOutcomeText(
  result: InvitationResult,
  formatInstant: (iso: string | null) => string | null,
): InvitationOutcomeText {
  const base = deliveryTextFor(result.delivery);
  const at = formatInstant(result.expiresAtUtc);
  return {
    ...base,
    expiryHe:
      at === null
        ? null
        : `הקישור בהזמנה תקף עד ${at}. שליחה חוזרת מנפיקה קישור חדש, והקודם מפסיק לפעול.`,
  };
}
